/**
 * Deadline Flag Calculator — consistent urgency logic across the app.
 *
 * Returns: { level: 'red'|'amber'|'green'|'gray', label: string, daysLeft: number|null }
 *
 * Logic:
 *   Red = overdue OR blocked (material risk / delivery_at_risk)
 *   Amber = 2 days or less remaining OR remaining stages > remaining days
 *   Green = 3+ days remaining, on track
 *   Gray = no committed date
 */

const STAGE_ORDER = ['new', 'casting', 'stone_setting', 'polishing', 'rhodium', 'final_qc', 'completed'];

export const getDeadlineFlag = (order) => {
  const committedDate = order.committed_delivery_date;
  if (!committedDate) return { level: 'gray', label: 'No date', daysLeft: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadline = new Date(committedDate);
  deadline.setHours(0, 0, 0, 0);
  const diffMs = deadline - today;
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Red: overdue or blocked
  if (daysLeft < 0) {
    return { level: 'red', label: `${Math.abs(daysLeft)}d overdue`, daysLeft };
  }
  if (order.delivery_at_risk || (order.material_risk_flag && daysLeft <= 3)) {
    return { level: 'red', label: daysLeft === 0 ? 'Due today — at risk' : `${daysLeft}d left — at risk`, daysLeft };
  }

  // Calculate remaining stages
  const currentStage = order.current_stage || 'new';
  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  const completedIdx = STAGE_ORDER.indexOf('completed');
  const remainingStages = currentIdx >= 0 ? completedIdx - currentIdx : 0;

  // Amber: 2 days or less, OR remaining stages > remaining days
  if (daysLeft <= 2) {
    return { level: 'amber', label: daysLeft === 0 ? 'Due today' : `${daysLeft}d left`, daysLeft };
  }
  if (remainingStages > daysLeft) {
    return { level: 'amber', label: `${daysLeft}d left, ${remainingStages} stages`, daysLeft };
  }

  // Green: on track
  return { level: 'green', label: `${daysLeft}d left`, daysLeft };
};

/**
 * Badge styles for each level
 */
export const DEADLINE_STYLES = {
  red: 'bg-red-100 text-red-700 border-red-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  gray: 'bg-gray-100 text-gray-500 border-gray-200',
};
